import { supabase } from '@/integrations/supabase/client';
import type {
  Plan,
  PlanChannelConfig,
  PlanTopic,
  PlanItem,
  PlanNotification,
  PlanCalendarRequest,
  PlanCalendarResponse,
  PlanDraftPayload,
  PlanHomeData,
} from '@/types/plan';

const PLAN_TABLE = 'writing_plans' as const;
const PLAN_CHANNEL_TABLE = 'writing_plan_channels' as const;
const PLAN_TOPIC_TABLE = 'writing_plan_topics' as const;
const PLAN_ITEM_TABLE = 'writing_plan_items' as const;
const PLAN_NOTIFICATION_TABLE = 'writing_plan_notifications' as const;

function sanitizePlanningTitle(title: string): string {
  const normalized = String(title || '').replace(/\s+/g, ' ').trim();
  if (!normalized) return '';

  const cleaned = normalized
    .replace(/^a\s+fresh\s+.+?\s+angle\s*[:\-–]\s*/i, '')
    .replace(/^fresh\s+.+?\s+angle\s*[:\-–]\s*/i, '')
    .replace(/^notes?\s+angle\s*[:\-–]\s*/i, '')
    .replace(/\s*[:\-–]\s*a\s+fresh\s+.+?\s+angle$/i, '')
    .replace(/\s*[:\-–]\s*fresh\s+.+?\s+angle$/i, '')
    .replace(/\s+notes?\s+angle$/i, '')
    .replace(/\s+angle$/i, '')
    .replace(/\s*[:\-–]\s*idea$/i, '')
    .replace(/\s*[:\-–]\s*draft$/i, '')
    .trim();

  return cleaned || normalized;
}

export type PlanMutationResult = PlanHomeData & {
  calendar_seed_warning?: string;
};

async function ensureCanUsePlanAI(userId: string): Promise<void> {
  const { data, error } = await supabase.rpc('check_user_can_proceed', {
    p_user_id: userId,
  });

  if (error || !data?.[0]?.can_proceed) {
    throw new Error(data?.[0]?.error_message || error?.message || 'Plan is not available for your account.');
  }
}

async function syncPlanProject(planId: string): Promise<void> {
  const { error } = await supabase.rpc('sync_plan_project_defaults', {
    p_plan_id: planId,
  });

  if (error) {
    console.error('[plans api] Failed syncing plan project defaults:', error);
  }
}

async function seedPlanCalendar(planId: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke('plan-seed-calendar', {
    method: 'POST',
    body: {
      planId,
      horizonDays: 28,
    },
  });

  if (error) {
    throw new Error(error.message || 'Failed to seed plan calendar.');
  }

  if (data?.error) {
    throw new Error(String(data.error));
  }
}

async function replaceChannels(planId: string, channels: PlanChannelConfig[]) {
  const { error: deleteError } = await supabase
    .from(PLAN_CHANNEL_TABLE)
    .delete()
    .eq('plan_id', planId);

  if (deleteError) throw new Error(deleteError.message);

  if (!channels.length) return;

  const payload = channels.map((channel) => ({
    plan_id: planId,
    channel: channel.channel,
    cadence_per_week: channel.cadence_per_week,
    preferred_days: channel.preferred_days,
    preferred_time_start: channel.preferred_time_start,
    preferred_time_end: channel.preferred_time_end,
    output_type: channel.output_type,
    length_preset: channel.length_preset,
    custom_length_value: channel.custom_length_value ?? null,
    custom_length_unit: channel.custom_length_unit ?? null,
    is_active: channel.is_active ?? true,
  }));

  const { error } = await supabase
    .from(PLAN_CHANNEL_TABLE)
    .insert(payload);

  if (error) throw new Error(error.message);
}

async function replaceTopics(planId: string, topics: PlanTopic[]) {
  const { error: deleteError } = await supabase
    .from(PLAN_TOPIC_TABLE)
    .delete()
    .eq('plan_id', planId);

  if (deleteError) throw new Error(deleteError.message);

  const cleaned = topics
    .map((topic) => topic.topic?.trim())
    .filter(Boolean);

  if (!cleaned.length) return;

  const payload = cleaned.map((topic) => ({
    plan_id: planId,
    topic,
    is_suggested: false,
  }));

  const { error } = await supabase
    .from(PLAN_TOPIC_TABLE)
    .insert(payload);

  if (error) throw new Error(error.message);
}

async function replaceCalendarItems(planId: string, items: PlanDraftPayload['calendar_items']) {
  if (!items) return;

  const { error: deleteError } = await supabase
    .from(PLAN_ITEM_TABLE)
    .delete()
    .eq('plan_id', planId)
    .eq('status', 'scheduled');

  if (deleteError) throw new Error(deleteError.message);

  if (!items.length) return;

  const payload = items.map((item) => ({
    plan_id: planId,
    channel: item.channel,
    title: sanitizePlanningTitle(item.title),
    brief: item.brief,
    scheduled_for: item.scheduled_for,
    status: 'scheduled',
    output_type: item.output_type,
    length_preset: item.length_preset,
    custom_length_value: item.custom_length_value ?? null,
    custom_length_unit: item.custom_length_unit ?? null,
    estimated_credits: item.estimated_credits ?? 0,
  }));

  const { error } = await supabase
    .from(PLAN_ITEM_TABLE)
    .insert(payload);

  if (error) throw new Error(error.message);
}

export async function listPlans(): Promise<Plan[]> {
  const { data, error } = await supabase
    .from(PLAN_TABLE)
    .select('*')
    .order('updated_at', { ascending: false });

  if (error) throw new Error(error.message);
  return (data || []) as Plan[];
}

export async function getPlan(planId: string): Promise<PlanHomeData | null> {
  const [planRes, channelsRes, topicsRes, itemsRes, notificationRes, unreadRes] = await Promise.all([
    supabase
      .from(PLAN_TABLE)
      .select('*')
      .eq('id', planId)
      .single(),
    supabase
      .from(PLAN_CHANNEL_TABLE)
      .select('*')
      .eq('plan_id', planId)
      .order('channel', { ascending: true }),
    supabase
      .from(PLAN_TOPIC_TABLE)
      .select('*')
      .eq('plan_id', planId)
      .order('created_at', { ascending: true }),
    supabase
      .from(PLAN_ITEM_TABLE)
      .select('*')
      .eq('plan_id', planId)
      .order('scheduled_for', { ascending: true })
      .limit(100),
    supabase
      .from(PLAN_NOTIFICATION_TABLE)
      .select('*')
      .eq('plan_id', planId)
      .order('created_at', { ascending: false })
      .limit(100),
    supabase
      .from(PLAN_NOTIFICATION_TABLE)
      .select('id', { count: 'exact', head: true })
      .eq('plan_id', planId)
      .eq('is_read', false),
  ]);

  if (planRes.error) {
    const errorCode = (planRes.error as { code?: string }).code;
    if (errorCode === 'PGRST116') return null;
    throw new Error(planRes.error.message);
  }

  if (channelsRes.error) throw new Error(channelsRes.error.message);
  if (topicsRes.error) throw new Error(topicsRes.error.message);
  if (itemsRes.error) throw new Error(itemsRes.error.message);
  if (notificationRes.error) throw new Error(notificationRes.error.message);
  if (unreadRes.error) throw new Error(unreadRes.error.message);

  return {
    plan: planRes.data as Plan,
    channels: (channelsRes.data || []) as PlanChannelConfig[],
    topics: (topicsRes.data || []) as PlanTopic[],
    items: ((itemsRes.data || []) as PlanItem[]).map((item) => ({
      ...item,
      title: sanitizePlanningTitle(item.title),
    })),
    notifications: (notificationRes.data || []) as PlanNotification[],
    unread_count: unreadRes.count || 0,
  };
}

export async function createPlan(payload: PlanDraftPayload): Promise<PlanMutationResult> {
  const { data: authData } = await supabase.auth.getUser();
  const userId = authData?.user?.id;
  if (!userId) throw new Error('You must be signed in to create a plan.');

  await ensureCanUsePlanAI(userId);

  const planInsert = {
    user_id: userId,
    name: payload.plan.name,
    status: payload.activate ? 'active' : 'draft',
    identity_type: payload.plan.identity_type,
    identity_id: payload.plan.identity_id,
    timezone: payload.plan.timezone || 'UTC',
    use_news_trends: payload.plan.use_news_trends,
    evergreen_only: payload.plan.evergreen_only,
    audience_description: payload.plan.audience_description || null,
    purpose_primary: payload.plan.purpose_primary || null,
    purpose_secondary: payload.plan.purpose_secondary || null,
    constraints: payload.plan.constraints || [],
    custom_constraints: payload.plan.custom_constraints || null,
    max_credits_per_item: payload.plan.max_credits_per_item,
    max_credits_per_week: payload.plan.max_credits_per_week,
    budget_strategy: payload.plan.budget_strategy,
    email_on_draft_ready: payload.plan.email_on_draft_ready,
  };

  const { data, error } = await supabase
    .from(PLAN_TABLE)
    .insert(planInsert)
    .select('*')
    .single();

  if (error || !data) throw new Error(error?.message || 'Failed to create plan');

  const planId = data.id as string;

  await Promise.all([
    replaceChannels(planId, payload.plan.channels),
    replaceTopics(planId, payload.plan.topics),
    replaceCalendarItems(planId, payload.calendar_items),
  ]);

  await syncPlanProject(planId);
  let seedWarning: string | undefined;
  try {
    await seedPlanCalendar(planId);
  } catch (seedError) {
    console.error('[plans api] Calendar seeding failed after create:', seedError);
    seedWarning = seedError instanceof Error ? seedError.message : 'Failed to seed plan calendar.';
  }

  const planData = await getPlan(planId);
  if (!planData) throw new Error('Failed to load created plan.');
  return {
    ...planData,
    calendar_seed_warning: seedWarning,
  };
}

export async function updatePlan(planId: string, payload: PlanDraftPayload): Promise<PlanMutationResult> {
  const { data: authData } = await supabase.auth.getUser();
  const userId = authData?.user?.id;
  if (!userId) throw new Error('You must be signed in to update a plan.');

  await ensureCanUsePlanAI(userId);

  const planUpdates: Record<string, unknown> = {
    name: payload.plan.name,
    identity_type: payload.plan.identity_type,
    identity_id: payload.plan.identity_id,
    timezone: payload.plan.timezone || 'UTC',
    use_news_trends: payload.plan.use_news_trends,
    evergreen_only: payload.plan.evergreen_only,
    audience_description: payload.plan.audience_description || null,
    purpose_primary: payload.plan.purpose_primary || null,
    purpose_secondary: payload.plan.purpose_secondary || null,
    constraints: payload.plan.constraints || [],
    custom_constraints: payload.plan.custom_constraints || null,
    max_credits_per_item: payload.plan.max_credits_per_item,
    max_credits_per_week: payload.plan.max_credits_per_week,
    budget_strategy: payload.plan.budget_strategy,
    email_on_draft_ready: payload.plan.email_on_draft_ready,
  };

  if (payload.activate) {
    planUpdates.status = 'active';
  }

  const { error } = await supabase
    .from(PLAN_TABLE)
    .update(planUpdates)
    .eq('id', planId);

  if (error) throw new Error(error.message);

  await Promise.all([
    replaceChannels(planId, payload.plan.channels),
    replaceTopics(planId, payload.plan.topics),
    replaceCalendarItems(planId, payload.calendar_items),
  ]);

  await syncPlanProject(planId);
  let seedWarning: string | undefined;
  try {
    await seedPlanCalendar(planId);
  } catch (seedError) {
    console.error('[plans api] Calendar seeding failed after update:', seedError);
    seedWarning = seedError instanceof Error ? seedError.message : 'Failed to seed plan calendar.';
  }

  const planData = await getPlan(planId);
  if (!planData) throw new Error('Failed to load updated plan.');
  return {
    ...planData,
    calendar_seed_warning: seedWarning,
  };
}

export async function deletePlan(planId: string): Promise<void> {
  const { error } = await supabase
    .from(PLAN_TABLE)
    .delete()
    .eq('id', planId);

  if (error) throw new Error(error.message);
}

export async function pausePlan(planId: string): Promise<void> {
  const { error } = await supabase
    .from(PLAN_TABLE)
    .update({ status: 'paused' })
    .eq('id', planId);

  if (error) throw new Error(error.message);
}

export async function resumePlan(planId: string): Promise<void> {
  const { error } = await supabase
    .from(PLAN_TABLE)
    .update({ status: 'active' })
    .eq('id', planId);

  if (error) throw new Error(error.message);
}

export async function generatePlanCalendarSuggestion(payload: PlanCalendarRequest): Promise<PlanCalendarResponse> {
  const { data, error } = await supabase.functions.invoke('plan-calendar', {
    method: 'POST',
    body: payload,
  });

  if (error) throw new Error(error.message || 'Failed to generate calendar suggestions.');
  return data as PlanCalendarResponse;
}

export async function runPlanItemNow(params: {
  planItemId: string;
  overrideBudget?: boolean;
}): Promise<{ success: boolean; processed: number; results: Array<{ itemId: string; ok: boolean; error?: string }> }> {
  const { data, error } = await supabase.functions.invoke('plan-runner', {
    method: 'POST',
    body: {
      mode: 'manual',
      planItemId: params.planItemId,
      overrideBudget: params.overrideBudget ?? false,
    },
  });

  if (error) throw new Error(error.message || 'Failed to run plan item.');
  return data;
}

export async function getNextScheduledPlanItem(planId: string): Promise<PlanItem | null> {
  const { data, error } = await supabase
    .from(PLAN_ITEM_TABLE)
    .select('*')
    .eq('plan_id', planId)
    .eq('status', 'scheduled')
    .gte('scheduled_for', new Date().toISOString())
    .order('scheduled_for', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) throw new Error(error.message);
  return (data || null) as PlanItem | null;
}

export async function listPlanNotifications(planId?: string, limit: number = 50): Promise<PlanNotification[]> {
  let query = supabase
    .from(PLAN_NOTIFICATION_TABLE)
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (planId) {
    query = query.eq('plan_id', planId);
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);

  return (data || []) as PlanNotification[];
}

export async function markPlanNotificationsRead(notificationIds?: string[]): Promise<void> {
  let query = supabase
    .from(PLAN_NOTIFICATION_TABLE)
    .update({
      is_read: true,
      read_at: new Date().toISOString(),
    })
    .eq('is_read', false);

  if (notificationIds && notificationIds.length > 0) {
    query = query.in('id', notificationIds);
  }

  const { error } = await query;
  if (error) throw new Error(error.message);
}

export async function getUnreadPlanNotificationCount(planId?: string): Promise<number> {
  let query = supabase
    .from(PLAN_NOTIFICATION_TABLE)
    .select('id', { count: 'exact', head: true })
    .eq('is_read', false);

  if (planId) {
    query = query.eq('plan_id', planId);
  }

  const { count, error } = await query;
  if (error) throw new Error(error.message);

  return count || 0;
}
